import { ENVIRONMENT_INITIALIZER, Injectable, Provider, inject, signal } from '@angular/core';
import { OSK_CORE_ICONS } from './icons/osk-icons-core';
import { OSKIconDefinition } from './osk-icon.types';

function toIconMap(icons: readonly OSKIconDefinition[]): Map<string, OSKIconDefinition> {
  return new Map(icons.map((icon) => [icon.name, icon]));
}

/**
 * Icons available to <osk-icon>.
 * - The few icons @oskey/ui components use themselves are always registered.
 * - Any other built-in icon is loaded on first use, from one lazy chunk holding the full set,
 *   so pages that never render it never download it.
 * - Register your own icons (or built-ins you want without the lazy load) with `provideOskIcons()`.
 */
@Injectable({ providedIn: 'root' })
export class OSKIconRegistry {
  private readonly icons = signal<ReadonlyMap<string, OSKIconDefinition>>(toIconMap(OSK_CORE_ICONS));
  private builtInsLoad: Promise<void> | null = null;

  register(icons: readonly OSKIconDefinition[]): void {
    this.icons.update((current) => new Map([...current, ...toIconMap(icons)]));
  }

  /** Reactive lookup: read in a template or computed, it updates once the icon gets registered. */
  get(name: string): OSKIconDefinition | undefined {
    return this.icons().get(name);
  }

  /** Loads every built-in icon from its lazy chunk, once. Icons the app registered are kept. */
  loadBuiltIns(): Promise<void> {
    this.builtInsLoad ??= import('./icons/osk-icons-all').then(({ OSK_ALL_ICONS }) => {
      const current = this.icons();
      this.register(OSK_ALL_ICONS.filter((icon) => !current.has(icon.name)));
    });
    return this.builtInsLoad;
  }
}

/**
 * Registers icons app-wide. Use it in `app.config.ts` or route providers (not component providers).
 * @example
 * provideOskIcons([oskIconTruck, { name: 'my-logo', content: '<path d="…" />' }])
 */
export function provideOskIcons(icons: readonly OSKIconDefinition[]): Provider {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: (): void => inject(OSKIconRegistry).register(icons)
  };
}
